import * as THREE from 'three';
import { TilesRenderer } from '3d-tiles-renderer';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import type { PointCloudViewer } from '../../services/pointcloud-runtime.js';
import { environment } from '../../../environments/environment.js';
import { SWISS_PROJECTION, type SwissConverter } from './swiss-projection.js';

/**
 * Manages 3D Tiles terrain data from Swisstopo
 * Integrates with the point cloud viewer to display terrain alongside point clouds
 */
export class TilesManager {
  viewer: PointCloudViewer;
  tilesRenderer: TilesRenderer | null;
  tilesGroup: THREE.Group;
  enabled: boolean;
  loading: boolean;
  tilesetUrl: string;
  wgs84ToLV95: SwissConverter;
  lv95ToWGS84: SwissConverter;
  wgs84ToECEF: SwissConverter;
  ecefToWGS84: SwissConverter;
  dracoLoader: DRACOLoader | null;
  gltfLoader: GLTFLoader | null;
  lv95Origin: THREE.Vector3 | null;
  tilesWorldMatrix: THREE.Matrix4 | null;
  lv95ToEcefFrame: THREE.Matrix4 | null;
  _activeCamera: THREE.Camera | null;

  constructor(viewer: PointCloudViewer) {
    this.viewer = viewer;
    this.tilesRenderer = null;
    this.tilesGroup = new THREE.Group();
    this.tilesGroup.name = 'SwissTerrainTiles';
    this.enabled = false;
    this.loading = false;

    // Swisstopo 3D tiles URL
    this.tilesetUrl = environment.terrain.buildingsTilesetUrl;

    // Create transformation functions (LV95 <-> WGS84 geodetic)
    this.wgs84ToLV95 = SWISS_PROJECTION.wgs84ToLv95;
    this.lv95ToWGS84 = SWISS_PROJECTION.lv95ToWgs84;

    this.wgs84ToECEF = SWISS_PROJECTION.wgs84ToEcef;
    this.ecefToWGS84 = SWISS_PROJECTION.ecefToWgs84;

    // Store reference to loaders for disposal
    this.dracoLoader = null;
    this.gltfLoader = null;

    // LV95 reference point
    this.lv95Origin = null;

    // World matrix used to bring tiles from ECEF into LV95 frame
    this.tilesWorldMatrix = null;

    // LV95-local ↔ ECEF frame for coordinate transformations
    this.lv95ToEcefFrame = null;

    // Camera currently registered with the tiles renderer
    this._activeCamera = null;

    // Initialize the tiles group in the scene
    if (this.viewer.scene && this.viewer.scene.scene) {
      this.viewer.scene.scene.add(this.tilesGroup);

      const tilesDirectionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
      tilesDirectionalLight.position.set(1, 1, 1).normalize();
      this.tilesGroup.add(tilesDirectionalLight);

      const tilesAmbientLight = new THREE.AmbientLight(0xffffff, 0.5);
      this.tilesGroup.add(tilesAmbientLight);
    }
  }

  /**
   * Set the LV95 origin from a point cloud's offset
   */
  setOriginFromPointCloud(pointCloud: { pcoGeometry?: { offset?: THREE.Vector3 } }): void {
    const origin = SWISS_PROJECTION.readOrigin(pointCloud);
    if (!origin) {
      console.warn('TilesManager: Could not get offset from point cloud');
      return;
    }

    const changed = !this.lv95Origin || !this.lv95Origin.equals(origin);
    this.lv95Origin = origin;

    if (changed && this.tilesRenderer?.root) {
      this._updateTransformationMatrix();
    }
  }

  /**
   * Make sure an LV95 origin exists. Falls back to the first available point
   * cloud and finally to the LV95 projection center, so tiles can always be
   * placed. The origin is refined again as soon as a point cloud is added.
   */
  private _ensureOrigin(): void {
    if (this.lv95Origin) {
      return;
    }

    const pointClouds = this.viewer?.scene?.pointclouds ?? [];
    this.lv95Origin = SWISS_PROJECTION.resolveOrigin(pointClouds);
  }

  /**
   * Load and initialize the 3D tiles
   */
  async loadTiles(): Promise<void> {
    if (this.loading || this.tilesRenderer) {
      return;
    }

    // Ensure we have an origin set
    this._ensureOrigin();

    this.loading = true;
    try {
      // Create the tiles renderer
      this.tilesRenderer = new TilesRenderer(this.tilesetUrl);

      // Configure DRACO loader for compressed geometries
      this.dracoLoader = new DRACOLoader();
      this.dracoLoader.setDecoderPath(environment.terrain.dracoDecoderPath);
      this.dracoLoader.setDecoderConfig({ type: 'js' });

      // Configure the GLTF loader with DRACO support
      this.gltfLoader = new GLTFLoader(this.tilesRenderer.manager);
      this.gltfLoader.setDRACOLoader(this.dracoLoader);

      // Set the custom GLTF loader on the tiles renderer
      this.tilesRenderer.manager.addHandler(/\.gltf$|\.glb$/i, this.gltfLoader);

      // Configure the tiles renderer
      this.tilesRenderer.fetchOptions.mode = 'cors';

      // Set LOD settings
      this.tilesRenderer.errorTarget = 2;
      this.tilesRenderer.maxDepth = 25;
      this.tilesRenderer.errorThreshold = Infinity;

      const tilesRenderer = this.tilesRenderer;

      // Listen for tileset load to get coordinate info
      tilesRenderer.addEventListener('load-tile-set', () => {
        if (tilesRenderer.root) {
          this._updateTransformationMatrix();
        }
      });

      // Handle loaded models
      tilesRenderer.addEventListener('load-model', (event: { scene: THREE.Object3D }) => {
        const sceneGroup = event.scene as THREE.Group;
        this._placeTileScene(sceneGroup);

        event.scene.traverse(child => {
          const mesh = child as THREE.Mesh;
          if (mesh.isMesh) {
            mesh.castShadow = false;
            mesh.receiveShadow = false;

            const material = mesh.material as THREE.MeshStandardMaterial | undefined;
            if (material) {
              material.side = THREE.DoubleSide;
              material.needsUpdate = true;
            }
          }
        });
      });

      // Add the tiles group to the scene
      this.tilesGroup.add(tilesRenderer.group);

      // Set camera for LOD calculation
      const camera = this.viewer.scene ? this.viewer.scene.getActiveCamera() : null;
      if (camera) {
        this._syncCamera(camera);
      }

      this.enabled = true;
      this.loading = false;

      // Force an initial update to kick off tile loading
      if (camera) {
        tilesRenderer.update();
      }

      // Dispatch event
      this.viewer.dispatchEvent({
        type: 'tiles_loaded',
        tilesManager: this,
      });
    } catch (error) {
      console.error('Error loading 3D tiles:', error);
      this.loading = false;
      this.enabled = false;
      throw error;
    }
  }

  /**
   * Internal method to compute and apply the ECEF to LV95 transformation matrix
   */
  _updateTransformationMatrix(): void {
    this._ensureOrigin();
    if (!this.lv95Origin) {
      console.error('TilesManager: Cannot update transformation matrix - no LV95 origin set');
      return;
    }

    try {
      if (!this.tilesRenderer) {
        return;
      }

      // Frame used for coarse placement, LOD and frustum culling.
      this.lv95ToEcefFrame = this._tangentFrame(this.lv95Origin);

      // ECEF → LV95-local (inverts the frame)
      this.tilesWorldMatrix = this.lv95ToEcefFrame.clone().invert();

      const group = this.tilesRenderer.group;
      group.matrixAutoUpdate = false;
      group.matrix.copy(this.tilesWorldMatrix);
      group.matrixWorldNeedsUpdate = true;

      // Position the tilesGroup at the LV95 origin
      this.tilesGroup.position.set(this.lv95Origin.x, this.lv95Origin.y, this.lv95Origin.z);
      this.tilesGroup.updateMatrixWorld(true);

      // Already loaded tiles were placed against the previous frame
      for (const scene of group.children as THREE.Group[]) {
        this._placeTileScene(scene);
      }
    } catch (e) {
      console.error('Error computing tiles ECEF→LV95 transform matrix:', e);
    }
  }

  /**
   * Build the local frame (LV95-local → WGS84 ECEF) tangent to the ellipsoid at
   * the given LV95 point, by numerical differentiation of the projection.
   */
  private _tangentFrame(lv95Point: THREE.Vector3): THREE.Matrix4 {
    const lv95ToEcef = (coords: number[]) => {
      const wgs84 = this.lv95ToWGS84(coords);
      return this.wgs84ToECEF([wgs84[0], wgs84[1], wgs84[2] ?? coords[2]]);
    };

    const lv95 = [lv95Point.x, lv95Point.y, lv95Point.z];
    const ecefP0 = lv95ToEcef(lv95);
    const ecefPx = lv95ToEcef([lv95[0] + 1, lv95[1], lv95[2]]);
    const ecefPy = lv95ToEcef([lv95[0], lv95[1] + 1, lv95[2]]);
    const ecefPz = lv95ToEcef([lv95[0], lv95[1], lv95[2] + 1]);

    // LV95 axis directions in ECEF
    const ex = [ecefPx[0] - ecefP0[0], ecefPx[1] - ecefP0[1], ecefPx[2] - ecefP0[2]];
    const ey = [ecefPy[0] - ecefP0[0], ecefPy[1] - ecefP0[1], ecefPy[2] - ecefP0[2]];
    const ez = [ecefPz[0] - ecefP0[0], ecefPz[1] - ecefP0[1], ecefPz[2] - ecefP0[2]];

    // prettier-ignore
    return new THREE.Matrix4().set(
      ex[0], ey[0], ez[0], ecefP0[0],
      ex[1], ey[1], ez[1], ecefP0[1],
      ex[2], ey[2], ez[2], ecefP0[2],
      0, 0, 0, 1,
    );
  }

  /**
   * Convert a WGS84 ECEF position to LV95.
   */
  private _ecefToLv95(ecef: THREE.Vector3): THREE.Vector3 {
    const wgs84 = this.ecefToWGS84([ecef.x, ecef.y, ecef.z]);
    const lv95 = this.wgs84ToLV95([wgs84[0], wgs84[1], wgs84[2]]);
    return new THREE.Vector3(lv95[0], lv95[1], lv95[2] ?? wgs84[2]);
  }

  /**
   * Re-place a loaded tile against a tangent frame built at the tile's own
   * position instead of the global one.
   *
   * A single tangent plane is only exact at its origin: a tile `d` metres away
   * sinks by roughly `d² / 2R` (~1 km at 110 km), which buries the buildings
   * below the terrain. Anchoring the frame per tile keeps the error within the
   * extent of a single tile (a few centimetres).
   */
  private _placeTileScene(scene: THREE.Group): void {
    if (!this.lv95Origin || !this.lv95ToEcefFrame) {
      return;
    }

    // The renderer sets scene.matrix to the tile's ECEF transform on load; keep
    // it around so the placement can be recomputed if the origin changes.
    let ecefMatrix: THREE.Matrix4 = scene.userData.tilesEcefMatrix;
    if (!ecefMatrix) {
      ecefMatrix = scene.matrix.clone();
      scene.userData.tilesEcefMatrix = ecefMatrix;
    }

    const tileEcef = new THREE.Vector3().setFromMatrixPosition(ecefMatrix);
    const tileLv95 = this._ecefToLv95(tileEcef);
    if (!Number.isFinite(tileLv95.x) || !Number.isFinite(tileLv95.z)) {
      return;
    }

    // ECEF → tile-local (exact at the tile) → offset from the global origin →
    // back into the frame the group matrix expects.
    const toTileLocal = this._tangentFrame(tileLv95).invert();
    const offsetFromOrigin = new THREE.Matrix4().makeTranslation(
      tileLv95.x - this.lv95Origin.x,
      tileLv95.y - this.lv95Origin.y,
      tileLv95.z - this.lv95Origin.z,
    );

    scene.matrix
      .copy(ecefMatrix)
      .premultiply(toTileLocal)
      .premultiply(offsetFromOrigin)
      .premultiply(this.lv95ToEcefFrame);
    scene.matrix.decompose(scene.position, scene.quaternion, scene.scale);
    scene.updateMatrixWorld(true);
  }

  /**
   * Register the currently active viewer camera with the tiles renderer.
   *
   * TilesRenderer already converts the camera into the tile (ECEF) frame using
   * `group.matrixWorld`, so the scene camera must be passed as-is - transforming
   * it manually would apply the conversion twice and cull every tile.
   */
  private _syncCamera(camera: THREE.Camera): void {
    if (!this.tilesRenderer) {
      return;
    }

    if (this._activeCamera !== camera) {
      if (this._activeCamera) {
        this.tilesRenderer.deleteCamera(this._activeCamera);
      }
      this.tilesRenderer.setCamera(camera);
      this._activeCamera = camera;
    }

    if (this.viewer.renderer) {
      this.tilesRenderer.setResolutionFromRenderer(camera, this.viewer.renderer);
    }
  }

  /**
   * Update the tiles renderer - should be called every frame
   */
  update(): void {
    if (!this.tilesRenderer || !this.enabled) {
      return;
    }

    try {
      const camera = this.viewer.scene.getActiveCamera();
      if (!camera) {
        return;
      }

      this._syncCamera(camera);

      // Keep the ECEF→LV95 transform applied and the world matrix in sync before
      // the renderer builds its frustums from it.
      const group = this.tilesRenderer.group;
      if (this.tilesWorldMatrix && !group.matrix.equals(this.tilesWorldMatrix)) {
        group.matrixAutoUpdate = false;
        group.matrix.copy(this.tilesWorldMatrix);
        group.matrixWorldNeedsUpdate = true;
      }
      group.updateMatrixWorld();

      // Update tiles - this handles LOD and downloads
      this.tilesRenderer.update();
    } catch (error) {
      console.error('Error updating 3D tiles:', error);
    }
  }

  /**
   * Enable or disable the terrain display
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.tilesGroup) {
      this.tilesGroup.visible = enabled;
    }
  }

  /**
   * Check if tiles are currently enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get the tiles group for direct manipulation
   */
  getGroup(): THREE.Group {
    return this.tilesGroup;
  }

  /**
   * Dispose of the tiles and free resources
   */
  dispose(): void {
    if (this.tilesRenderer) {
      this.tilesRenderer.dispose();
      this.tilesRenderer = null;
    }

    this._activeCamera = null;

    if (this.dracoLoader) {
      this.dracoLoader.dispose();
      this.dracoLoader = null;
    }

    if (this.gltfLoader) {
      this.gltfLoader = null;
    }

    if (this.tilesGroup) {
      if (this.viewer.scene && this.viewer.scene.scene) {
        this.viewer.scene.scene.remove(this.tilesGroup);
      }
    }

    this.enabled = false;
    this.loading = false;
  }

  /**
   * Reload the tiles (dispose and reload)
   */
  async reload(): Promise<void> {
    this.dispose();
    await this.loadTiles();
  }
}
